import { Cluster, getClusterLabel } from "in_queue/types/clusterType";
import {
    getProjectDisplayName,
    TrackedProject
} from "in_queue/types/projectType";
import { ScenarioMetadata, ScenarioStatus } from "in_queue/types/scenarioType";

export enum SearchCategory {
    CLUSTER = "cluster",
    PROJECT = "project",
    SCENARIO = "scenario"
}

export type ClusterSearchStatus = "active" | "past";

interface SearchItemBase {
    readonly id: string;
    readonly label: string;
    readonly searchText: string;
    readonly navigateTo: string;
    readonly secondaryLabel: string;
}

export interface ClusterSearchItem extends SearchItemBase {
    readonly category: SearchCategory.CLUSTER;
}

export interface ProjectSearchItem extends SearchItemBase {
    readonly category: SearchCategory.PROJECT;
    readonly fuelType: string;
}

export interface ScenarioSearchItem extends SearchItemBase {
    readonly category: SearchCategory.SCENARIO;
}

export type SearchItem =
    | ClusterSearchItem
    | ProjectSearchItem
    | ScenarioSearchItem;

export const createClusterItem = ({
    cluster,
    clusterStatus
}: {
    cluster: Cluster;
    clusterStatus: ClusterSearchStatus;
}): ClusterSearchItem => {
    const label = getClusterLabel(cluster);
    return {
        id: `cluster-${cluster.slug}`,
        category: SearchCategory.CLUSTER,
        label,
        searchText: label.toLowerCase(),
        navigateTo: `/portfolio/${cluster.slug}`,
        secondaryLabel: clusterStatus === "active" ? "Active" : "Past"
    };
};

export const createProjectItem = ({
    project,
    cluster
}: {
    project: TrackedProject;
    cluster: Cluster;
}): ProjectSearchItem => {
    const label = getProjectDisplayName(project);
    const clusterLabel = getClusterLabel(cluster);

    return {
        id: `project-${project.projectId}`,
        category: SearchCategory.PROJECT,
        label,
        searchText:
            `${label} ${clusterLabel} ${project.fuelType}`.toLowerCase(),
        navigateTo: `/portfolio/${cluster.slug}/${project.projectId}`,
        secondaryLabel: project.fuelType,
        fuelType: project.fuelType
    };
};

const getScenarioStatusLabel = (status: ScenarioStatus): string => {
    switch (status) {
        case ScenarioStatus.Draft:
            return "Draft";
        case ScenarioStatus.RunRequested:
        case ScenarioStatus.RunSubmitted:
            return "Queued";
        case ScenarioStatus.Running:
            return "Running";
        case ScenarioStatus.RunSucceeded:
            return "Done";
        case ScenarioStatus.RunFailed:
            return "Failed";
        case ScenarioStatus.Archived:
            return "Archived";
    }
};

export const createScenarioItem = ({
    scenario,
    navigateProjectId,
    cluster
}: {
    scenario: ScenarioMetadata;
    navigateProjectId: string;
    cluster: Cluster;
}): ScenarioSearchItem => {
    const clusterLabel = getClusterLabel(cluster);
    return {
        id: `scenario-${scenario.scenarioId}`,
        category: SearchCategory.SCENARIO,
        label: scenario.title,
        searchText: `${scenario.title} ${clusterLabel}`.toLowerCase(),
        navigateTo: `/portfolio/${cluster.slug}/${navigateProjectId}/scenario/${scenario.scenarioId}`,
        secondaryLabel: getScenarioStatusLabel(scenario.status)
    };
};
